import { Check } from '../../../icons';
import { isAddon, isMain, fmtDeliverableType } from '@discovr/types';
import type { DeliverableInstance } from '@discovr/types';
import { formatLineMoney } from '../../../lib/formatMoney';
import { DELIVERABLE_TYPE_META } from './deliverableTypeMeta';

const USAGE_RIGHTS_TYPE = 'usage_rights';
const DELIVERABLES_GROUP_LABEL = 'Deliverables';

type PickerDeliverable = DeliverableInstance & { days?: number };

export interface DeliverablePickerProps {
  deliverables: PickerDeliverable[];
  platform: string;
  /** Full selection: main type + any add-ons. */
  selected: string[];
  /** Replace the selection with a new full array (main + add-ons). */
  onChange: (types: string[]) => void;
  /** Freezes the picker (read-only members). */
  disabled?: boolean;
}

/**
 * Main (radio) + add-on (checkbox) deliverable picker with a selection
 * summary for the single-creator approval dialog. Add-ons stay locked until a
 * main is picked.
 */
export function DeliverablePicker({
  deliverables,
  platform,
  selected,
  onChange,
  disabled = false,
}: DeliverablePickerProps) {
  if (deliverables.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted p-3 text-center text-[11px] text-muted-foreground">
        No deliverables on this entry. Use the edit pencil to add prices first.
      </div>
    );
  }

  // No price guard — the BE only sends deliverables priced >= 0, so a ₹0
  // deliverable is a real option and must show.
  const mainDeliverables = deliverables.filter((d) => isMain(String(d.type ?? ''), platform));
  const addonDeliverables = deliverables.filter((d) => isAddon(String(d.type ?? ''), platform));
  const selectedMain = selected.find((t) => isMain(t, platform)) ?? '';
  const selectedAddons = selected.filter((t) => isAddon(t, platform));

  const handleMainToggle = (type: string) => {
    if (disabled) return;
    // Add-ons survive a main change so the user's add-on intent is kept.
    onChange(type === selectedMain ? [] : [type, ...selectedAddons]);
  };

  const handleAddonToggle = (type: string) => {
    if (disabled || !selectedMain) return;
    const next = selectedAddons.includes(type)
      ? selectedAddons.filter((t) => t !== type)
      : [...selectedAddons, type];
    onChange([selectedMain, ...next]);
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <div className="text-xs font-medium uppercase text-muted-foreground">Deliverables (select any one)</div>
        <div className="flex flex-col gap-1.5" role="radiogroup" aria-label={DELIVERABLES_GROUP_LABEL}>
          {mainDeliverables.map((d) => (
            <MainOption
              key={String(d.type)}
              deliverable={d}
              selected={selectedMain === d.type}
              disabled={disabled}
              onToggle={handleMainToggle}
            />
          ))}
        </div>
      </div>

      {addonDeliverables.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            <div className="text-xs font-medium uppercase text-muted-foreground">Add-ons</div>
            {!selectedMain && <span className="text-[10px] text-muted-foreground">(select a main first)</span>}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {addonDeliverables.map((d) => (
              <AddonOption
                key={String(d.type)}
                deliverable={d}
                checked={selectedAddons.includes(String(d.type))}
                disabled={!selectedMain || disabled}
                onToggle={handleAddonToggle}
              />
            ))}
          </div>
        </div>
      )}

      {selected.length > 0 && (
        <SelectionSummary selected={selected} platform={platform} addonDeliverables={addonDeliverables} />
      )}
    </div>
  );
}

function mainOptionClass(selected: boolean, disabled: boolean): string {
  const border = selected ? 'border border-foreground bg-card' : 'border-[1.5px] border-border bg-card';
  if (disabled) return `cursor-not-allowed opacity-50 ${border}`;
  if (selected) return border;
  return `${border} hover:border-foreground/30`;
}

function MainOption({
  deliverable,
  selected,
  disabled,
  onToggle,
}: {
  deliverable: PickerDeliverable;
  selected: boolean;
  disabled: boolean;
  onToggle: (type: string) => void;
}) {
  const type = String(deliverable.type ?? '');
  const price = Number(deliverable.base_price ?? 0);
  const meta = DELIVERABLE_TYPE_META[type];
  const Glyph = meta?.Icon;
  const checkClass = selected ? 'bg-(--brand-500) text-white' : 'invisible';
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={() => onToggle(type)}
      disabled={disabled}
      className={`flex h-[58px] items-center gap-3 rounded-lg px-3 text-left transition-colors ${mainOptionClass(selected, disabled)}`}
    >
      {Glyph && (
        <span className="flex size-4 shrink-0 items-center justify-center text-foreground">
          <Glyph size={16} weight="regular" />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground">
          {meta?.label ?? fmtDeliverableType(type)}
        </span>
        {meta?.sub && <span className="block truncate text-xs text-muted-foreground">{meta.sub}</span>}
      </span>
      <span className="flex h-9 shrink-0 flex-col items-end justify-between">
        <span className={`flex size-3.5 items-center justify-center rounded-full ${checkClass}`} aria-hidden>
          {selected && <Check size={8} weight="bold" />}
        </span>
        {price > 0 && (
          <span className="text-xs font-medium tabular-nums text-foreground">{formatLineMoney({ amount: price, currency: deliverable.currency ?? 'INR' }, deliverable.converted)}</span>
        )}
      </span>
    </button>
  );
}

function addonOptionClass(checked: boolean, disabled: boolean): string {
  if (disabled) return 'cursor-not-allowed border-border bg-muted opacity-50';
  if (checked) return 'border-foreground bg-card text-foreground';
  return 'border-border bg-card text-foreground hover:border-foreground/30';
}

function hasUsageDays(type: string, days: number | undefined): days is number {
  return type === USAGE_RIGHTS_TYPE && days != null && days > 0;
}

function AddonOption({
  deliverable,
  checked,
  disabled,
  onToggle,
}: {
  deliverable: PickerDeliverable;
  checked: boolean;
  disabled: boolean;
  onToggle: (type: string) => void;
}) {
  const type = String(deliverable.type ?? '');
  const price = Number(deliverable.base_price ?? 0);
  const days = deliverable.days;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onToggle(type)}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-left transition-colors ${addonOptionClass(checked, disabled)}`}
    >
      {checked && <Check size={13} weight="bold" className="shrink-0 text-foreground" />}
      <span className="text-sm font-medium whitespace-nowrap">
        {fmtDeliverableType(type)}
        {hasUsageDays(type, days) && <span className="ml-1 font-medium text-muted-foreground">· {days}d</span>}
        {price > 0 && <span className="ml-1 font-semibold tabular-nums">{formatLineMoney({ amount: price, currency: deliverable.currency ?? 'INR' }, deliverable.converted)}</span>}
      </span>
    </button>
  );
}

function SelectionSummary({
  selected,
  platform,
  addonDeliverables,
}: {
  selected: string[];
  platform: string;
  addonDeliverables: PickerDeliverable[];
}) {
  const addonDays = (type: string) => addonDeliverables.find((d) => d.type === type)?.days;
  return (
    <div className="rounded-lg border border-border bg-muted px-3 py-2">
      <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Will approve for</div>
      <div className="flex flex-wrap gap-1.5">
        {selected
          .filter((t) => isMain(t, platform))
          .map((t) => (
            <span
              key={t}
              className="inline-flex items-center rounded-full bg-indigo-600 px-2.5 py-0.5 text-[11px] font-semibold text-white"
            >
              {fmtDeliverableType(t)}
            </span>
          ))}
        {selected
          .filter((t) => isAddon(t, platform))
          .map((t) => {
            const days = addonDays(t);
            const daysSuffix = hasUsageDays(t, days) ? ` · ${days}d` : '';
            return (
              <span
                key={t}
                className="inline-flex items-center rounded-full border border-border bg-card px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground"
              >
                + {fmtDeliverableType(t)}
                {daysSuffix}
              </span>
            );
          })}
      </div>
    </div>
  );
}
